import { ContractType, SignatureType } from '@dealstack/shared/lib/generated';
import {
	allowsManualSignaturePlacement,
	isReviewSignatureType,
} from '@dealstack/shared/lib/common/signatures';
import { isEqual } from 'lodash';

// FORM contracts cannot use the REVIEW ("no signature required") signature type.
// If the user lands in that combination by switching type to FORM while REVIEW is
// selected, the signatureType must be reset to a valid value.
export const shouldResetReviewSignatureForForm = (
	type: ContractType,
	signatureType: SignatureType
): boolean => {
	return type === ContractType.FORM && isReviewSignatureType(signatureType);
};

// A freshly uploaded contract row only carries a client-side UUID until the draft is
// saved, so the persisted template has to be matched back by its (stable) file id.
export const findPersistedContractByFileId = <T extends { file: { id: string } }>(
	savedContracts: T[] | null,
	fileId: string
): T | null => {
	return savedContracts?.find((contract) => contract.file.id === fileId) ?? null;
};

interface ContractComparable {
	name: string;
	instruction: string;
	signatureType: SignatureType;
	type: ContractType;
	requiredWitnesses: number;
	counterparties?: Array<{ name: string; type: string }>;
}

// Saving a contract rebuilds its signing roles, and placements cascade off those roles —
// so every field that triggers a template update also clears the placements. Shared with
// extractContractsToUpdate so "will save" and "will lose placements" can't drift apart.
export const hasContractChangedFromSaved = (
	form: ContractComparable,
	saved: ContractComparable
): boolean => {
	const comparable = (contract: ContractComparable) => ({
		name: contract.name,
		instruction: contract.instruction,
		signatureType: contract.signatureType,
		type: contract.type,
		requiredWitnesses: contract.requiredWitnesses,
		counterparties: (contract.counterparties ?? []).map(({ name, type }) => ({ name, type })),
	});

	return !isEqual(comparable(form), comparable(saved));
};

// A MULTISIGNATURE contract is one document every signer shares, so it cannot carry per-signer
// coordinates. A FORM is placeable: with an ELECTRONIC signature it becomes a SIGNABLE_FORM
// (fill, then e-sign) whose pagination is stable, and with anything else the signature-type
// rule already rejects it — a wet-ink FORM is a non-signing FORM_INPUT.
export const canPlaceContractSignatures = ({
	type,
	signatureType,
}: {
	type: ContractType;
	signatureType: SignatureType;
}): boolean =>
	allowsManualSignaturePlacement({
		signatureType,
		isMultiSign: type === ContractType.MULTISIGNATURE,
	});
